import type { PersistStorage, StorageValue } from "zustand/middleware";
import { idbDelSafe as idbDel, idbGetSafe as idbGet, idbSetSafe as idbSet } from "./safeIdb";

/**
 * A `PersistStorage` that:
 *   1. Debounces rapid store updates into a single write
 *   2. Defers the `JSON.stringify` + IndexedDB put to `requestIdleCallback`,
 *      so user-initiated actions (minimize/close) return immediately instead
 *      of blocking on serialization of large session state
 *   3. Flushes synchronously on `pagehide`/`beforeunload` so nothing is lost
 *      when the tab goes away
 *
 * Trade-off: in the ~250 ms debounce window after the last write, a hard
 * crash (non-graceful tab close skipping `pagehide`) would lose the very
 * last update. Acceptable for draft email state.
 */
export function createDeferredPersistStorage<S>({
  debounceMs = 250,
  idleTimeoutMs = 500,
}: {
  debounceMs?: number;
  idleTimeoutMs?: number;
} = {}): PersistStorage<S> {
  type Pending = { name: string; value: StorageValue<S> } | { name: string; tombstone: true };
  const pending = new Map<string, Pending>();
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  const scheduleIdle = (cb: () => void) => {
    if (typeof window === "undefined") {
      setTimeout(cb, 0);
      return;
    }
    const ric = (
      window as typeof window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      }
    ).requestIdleCallback;
    if (ric) ric(cb, { timeout: idleTimeoutMs });
    else setTimeout(cb, 0);
  };

  const flushNow = async () => {
    if (pending.size === 0) return;
    const entries = Array.from(pending.values());
    pending.clear();
    for (const entry of entries) {
      if ("tombstone" in entry) {
        await idbDel(entry.name).catch(() => {});
      } else {
        const str = JSON.stringify(entry.value);
        await idbSet(entry.name, str).catch(() => {});
      }
    }
  };

  const scheduleFlush = () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      scheduleIdle(() => {
        flushNow();
      });
    }, debounceMs);
  };

  // Best-effort synchronous flush on page hide. Can't await async IDB here, but
  // we can at least kick off the writes so they have a chance to complete.
  if (typeof window !== "undefined") {
    const syncFlush = () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      for (const entry of Array.from(pending.values())) {
        if ("tombstone" in entry) {
          idbDel(entry.name).catch(() => {});
        } else {
          idbSet(entry.name, JSON.stringify(entry.value)).catch(() => {});
        }
      }
      pending.clear();
    };
    window.addEventListener("pagehide", syncFlush);
    window.addEventListener("beforeunload", syncFlush);
  }

  return {
    getItem: async (name) => {
      const raw = await idbGet<string>(name);
      if (raw == null) return null;
      try {
        return JSON.parse(raw) as StorageValue<S>;
      } catch {
        return null;
      }
    },
    setItem: (name, value) => {
      pending.set(name, { name, value });
      scheduleFlush();
    },
    removeItem: (name) => {
      pending.set(name, { name, tombstone: true });
      scheduleFlush();
    },
  };
}
